import "./PacingDashboard.scss";
import { Dropdown, DropdownToggleType, DownloadDropdown, FilterBar } from "../Components";
import { MdAdd, MdSort } from "react-icons/md";
import { Button, Modal } from "react-bootstrap";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PacingBarsDashboard from "./PacingBarsDashboard";
import { pacingSortByOptions } from "./pacingUtils";
import { downloadPNG, exportToExcel } from "../utils/download-utils";
import { useStateFunction } from "../utils/hooks/useData";
import { RouteComponentProps } from "@reach/router";
import { PacingDataEntry } from "@blisspointmedia/bpm-types/dist/Pacing";
import { measureText } from "./PacingBar";
import * as R from "ramda";

const GOAL_VALUE_OPTIONS = [
  { value: "Budget", label: "Budget" },
  { value: "CPX", label: "CPX" },
  { value: "ROAS", label: "ROAS" },
  { value: "CPC", label: "CPC" },
  { value: "CPM", label: "CPM" },
  { value: "CTR", label: "CTR" },
  { value: "KPI Volume", label: "KPI Volume" },
  { value: "Revenue", label: "Revenue" },
];

const GOAL_VALUE_MAP: Record<string, string[]> = {
  Budget: ["Actual Spend", "Projected Spend", "Remainder"],
  CPX: ["Actual", "", "Difference"],
  ROAS: ["Actual", "", "Difference"],
  CPC: ["Actual", "", "Difference"],
  CPM: ["Actual", "", "Difference"],
  CTR: ["Actual", "", "Difference"],
  "KPI Volume": ["Actual", "Projected", "Difference"],
  Revenue: ["Actual", "Projected", "Difference"],
};

interface PacingDashboardProps {
  pacingData: PacingDataEntry[];
  start: string;
  end: string;
  simplicity: string;
  customSegments: string[];
  isActual: boolean;
  showHelpModal: boolean;
  setShowHelpModal: React.Dispatch<React.SetStateAction<boolean>>;
  adjustedGranularBudget: PacingDataEntry[];
}

const PacingDashboard = ({
  pacingData,
  start,
  end,
  simplicity,
  customSegments,
  isActual,
  showHelpModal,
  setShowHelpModal,
  adjustedGranularBudget,
}: PacingDashboardProps & RouteComponentProps): JSX.Element => {
  const [longestText, setLongestText] = useState(0);
  const [segmentVisibility, setSegmentVisibility] = useState({});
  const [showMissingBudgetModal, setShowMissingBudgetModal] = useState(false);
  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);
  const [activeDimensions, setActiveDimensions] = useState<string[]>(["Channel"]);
  const [sort, setSort] = useState("Abc");
  const [currentGoalValue, setCurrentGoalValue] = useState<string>("Budget");

  useEffect(() => {
    let longest = 0;
    customSegments.forEach(segment => {
      const currentKey: string = segment;
      const segmentWordsSet = new Set(pacingData?.map(item => String(item[currentKey])));
      const uniqueSegmentWords = Array.from(segmentWordsSet);
      longest = R.max(
        uniqueSegmentWords.reduce(
          (accumulator, currentValue) =>
            (accumulator = R.max(accumulator, measureText(currentValue))),
          0
        ),
        longest
      );
    });
    setLongestText(longest);
  }, [customSegments, pacingData]);

  useEffect(() => {
    const filteredPacingDataByGoalName = pacingData.filter(
      item => item.goalName === currentGoalValue
    );

    if (filteredPacingDataByGoalName.length === 0) {
      setShowMissingBudgetModal(true);
    }
  }, [pacingData, currentGoalValue]);

  const toggleSegmentVisibility = index => {
    setSegmentVisibility(prevState => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const availableDimensions = useMemo(() => {
    const currentBudgets = pacingData.filter(item => item.goalName === currentGoalValue);
    const nullSegments = customSegments.filter(segment =>
      currentBudgets?.every(obj => obj[segment] === null)
    );
    const uniqueNonNullSegments = customSegments.filter(segment => !nullSegments.includes(segment));

    return uniqueNonNullSegments.map(segment => ({
      label: String(segment),
      value: String(segment),
    }));
  }, [pacingData, customSegments, currentGoalValue]);

  const handleDimensionChange = (index: number, newDimension: string) => {
    setActiveDimensions(prevDimensions => {
      const newDimensions = [...prevDimensions];
      newDimensions[index] = newDimension;
      return newDimensions;
    });
  };

  const handleRemoveDimension = (index: number) => {
    setActiveDimensions(prevDimensions => prevDimensions.filter((_, i) => i !== index));
  };

  const filteredDataByGoalNameAndSegments = useMemo(() => {
    return pacingData.filter(item => item.goalName === currentGoalValue).filter(filter) || [];
  }, [currentGoalValue, filter, pacingData]);

  const filterBarOptions = useMemo(() => {
    const uniqueSegmentOptions =
      customSegments?.map(segment => ({
        name: segment,
        label: segment,
      })) ?? [];

    const options = [...uniqueSegmentOptions];

    return options;
  }, [customSegments]);

  const excelDownloadPacingData = useCallback(() => {
    exportToExcel(pacingData, `pacing_${start}_${end}`);
  }, [pacingData, end, start]);

  const pngDownloadPacingData = useCallback(async () => {
    await downloadPNG(".bpmPage .body .crossChannelPacingDashboard", "pacingBars");
  }, []);

  const pacingBarsDashboardData = useMemo(() => {
    return (
      adjustedGranularBudget.filter(item => item.goalName === currentGoalValue).filter(filter) || []
    );
  }, [currentGoalValue, filter, adjustedGranularBudget]);

  const filteredDimensions = useMemo(() => {
    return availableDimensions.filter(dimension => !activeDimensions.includes(dimension.value));
  }, [availableDimensions, activeDimensions]);

  return (
    <div className="crossChannelPacingDashboard">
      <div className="pacingDashboardTitleContainer">
        <div className="pacingDashboardTitleContainerTopHalfTextAndButtons">
          <div className="dashboardTitleLeftHalf">
            <div className="dashboardTitleElements">Pacing&nbsp;to</div>
            <Dropdown
              type={DropdownToggleType.WIDGET_TITLE}
              value={currentGoalValue}
              options={GOAL_VALUE_OPTIONS}
              onChange={newGoalValue => setCurrentGoalValue(newGoalValue)}
            />
            {activeDimensions.map((dim, index) => (
              <div key={index} className="dashboardTitleElements">
                {index === 0 ? (
                  <>
                    by
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={activeDimensions[index]}
                      options={filteredDimensions}
                      onChange={newDimension => handleDimensionChange(index, newDimension)}
                    />
                  </>
                ) : (
                  <>
                    <div className="dashboardTitleElements">
                      then&nbsp;by
                      <Dropdown
                        type={DropdownToggleType.WIDGET_TITLE}
                        value={activeDimensions[index]}
                        options={filteredDimensions}
                        onChange={newDimension => handleDimensionChange(index, newDimension)}
                      />
                      <button
                        className="removeDimension"
                        onClick={() => handleRemoveDimension(index)}
                      >
                        x
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            <div className="dashboardTitleElements">
              {activeDimensions.length > 0 && activeDimensions.length < 4 && (
                <Dropdown
                  background="dark"
                  value=""
                  options={filteredDimensions}
                  onChange={newDimension =>
                    handleDimensionChange(activeDimensions.length, newDimension)
                  }
                  placeholder="Add dimension"
                  leadingIcon={<MdAdd />}
                />
              )}
            </div>
          </div>
          <div className="dashboardTitleButtons">
            <Dropdown
              label="Sort by"
              value={sort}
              options={pacingSortByOptions}
              onChange={change => setSort(change)}
              leadingIcon={<MdSort />}
            ></Dropdown>
          </div>
          <div className="dashboardTitleButtons">
            <DownloadDropdown
              size="lg"
              onClickOptions={[excelDownloadPacingData, pngDownloadPacingData]}
            />
          </div>
        </div>
        <div className="pacingDashboardBottomHalfFilterBarAndLegend">
          <FilterBar
            hasAdvanced={true}
            onFilter={setFilter}
            lines={filteredDataByGoalNameAndSegments || []}
            options={filterBarOptions}
          ></FilterBar>
          <div className="pacingLegend">
            <div className="pacingLegendContainer">
              <div className="pacingLegendHead">Legend:</div>
              <div className="pacingBoxLineText">
                <div className="pacingBox delivered" />
                <div className="legendLabel">{GOAL_VALUE_MAP[currentGoalValue][0]}</div>
              </div>
              {(currentGoalValue === "Budget" ||
                currentGoalValue === "Revenue" ||
                currentGoalValue === "KPI Volume") && (
                <div className="pacingBoxLineText">
                  <div className="pacingBox projected" />
                  <div className="legendLabel">{GOAL_VALUE_MAP[currentGoalValue][1]}</div>
                </div>
              )}
              <div className="pacingBoxLineText">
                <div className="pacingBox remainder" />
                <div className="legendLabel">{GOAL_VALUE_MAP[currentGoalValue][2]}</div>
              </div>
              <div className="pacingBoxLineText">
                <div className="pacingLine budget" />
                <div className="legendLabel legendBudgetText">Budget</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PacingBarsDashboard
        pacingData={pacingBarsDashboardData}
        keys={["goalName"]}
        simplicity={simplicity.toLocaleLowerCase()}
        marginLeft={1}
        segmentVisibility={segmentVisibility}
        toggleSegmentVisibility={toggleSegmentVisibility}
        longestText={longestText}
        sortOption={sort}
        total={true}
        dateEnd={end}
        goalName={currentGoalValue}
        isActual={isActual}
        combineSections={activeDimensions.length === 1}
      />
      {!showMissingBudgetModal ? (
        <PacingBarsDashboard
          pacingData={pacingBarsDashboardData}
          keys={activeDimensions}
          simplicity={simplicity.toLocaleLowerCase()}
          marginLeft={1}
          segmentVisibility={segmentVisibility}
          toggleSegmentVisibility={toggleSegmentVisibility}
          longestText={longestText}
          sortOption={sort}
          dateEnd={end}
          goalName={currentGoalValue}
          isActual={isActual}
          combineSections={activeDimensions.length === 1}
        />
      ) : (
        <Modal show size="lg" onHide={() => setShowMissingBudgetModal(false)}>
          {" "}
          <Modal.Header closeButton>
            <Modal.Title>{`There are no current budgets for ${currentGoalValue}`}</Modal.Title>
          </Modal.Header>
        </Modal>
      )}

      {showHelpModal && (
        <Modal
          show
          size="lg"
          onHide={() => setShowHelpModal(false)}
          className="pacingDashboardModal"
        >
          <Modal.Header closeButton>
            <Modal.Title>What Do These Options Mean?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              In general, "Simple" will figure out what the daily values for a flight should be, and
              compute booked and projected values from that. "Adjusted" will do various things such
              as taking overdelivery into account and focus more on what's left than what's already
              run.
            </p>
            <h2>Booked</h2>
            <p>
              In "Simple" mode, the amount booked will be the total booked for the flight, divided
              by the number of days in the flight, times the number of days in the date range. If
              the flight is from Monday through Sunday, and the date range is from Monday through
              Sunday, the booked amount will be exactly what's on the IO. If the flight is two weeks
              long, and the date range is only one week long, the amount booked will be half of
              what's on the IO. If the flight ends before the date range ends, it will not multiply
              by the full date range but only up to when the flight ends, so if the date range is
              two weeks, and the flight is one week, the booked will be the total booked on the IO.
            </p>
            <p>
              In "Adjusted" mode, it will first deduct any impressions served for that flight before
              the selected date range. It will then divide the remaining booked impressions by the
              number of days remaining in the flight, then multiply by the length of the date range.
              If the flight is contained entirely within the date range, this number should be the
              same as the simple version.
            </p>
            <p>
              Suppose there was a flight that was 10 days long and had a spend of $1,000. For 5 days
              it paced perfectly ($100 a day) and delivered a total of $500. Then we double the
              spend, making it $2,000. If our date range is the full ten days, both the Simple and
              Adjusted booked numbers will be $2,000. However, suppose the date range only covered
              the final 5 days. In Simple mode it would be the full flight spend ($2,000) divided by
              the number of days in the flight (10), times the number of days in our range (5),
              which would be $1,000 (or half the total spend). In Adjusted mode, it would be the
              full flight spend ($2,000) minus what was delivered outside of the date range ($500,
              for a remaining total of $1,500), divided by the remaining days in the flight (5),
              times the days in our date range (5), for a total of $1,500.
            </p>
            <h2>Projected</h2>
            <p>
              In "Simple" mode, we take the number of impressions served in the date range, divide
              by the number of days so far, then multiplied by the full date range. In "Adjusted"
              mode, we take that number, but cut it off at the booked amount, assuming that once the
              network hits the IO amount, they'll stop delivering.
            </p>
            <h2>Actual Delivery</h2>
            <p>
              The "Simple" and "Adjusted" delivered impressions will always be the same, because
              these are the number of impressions we got. The "Simple" spend will be that number
              times the cpm divided by 1000. The "Adjusted" spend will be that number, but with
              overdelivery taken into account.
            </p>
            <h2>Ideal Delivery</h2>
            <p>
              "Ideal Delivery" refers to the "Ideal Daily Delivery" and "Ideal Delivery" lines in
              the charts that appear when selecting an item in the right pane. In "Simple" mode, the
              ideal daily delivery will be the total spend of the flight divided by the number of
              days in the flight. The Ideal total delivery will be that number times the number of
              days. In other words, the ideal daily delivery line will be flat, and the ideal
              delivery line will be a straight increasing line. In "Adjusted" mode, instead of
              looking at what the pacing should be for the whole flight, it looks at what the pacing
              should be based on the remaining impressions. For each day it takes the number of
              remaining impressions, then divides by the number of remaining days. Thus, if a flight
              is under-delivering, the ideal line will get higher, and if it's over-delivering, it
              will get lower.
            </p>
            <p>
              Consider the above example with the 10 day flight at $1,000 that doubles in spend half
              way through. Looking at the last five days, "Simple" mode will compute the ideal daily
              spend as the total spend ($2,000) divided by the total days in the flight (10), to get
              the ideal daily spend ($200). So it will show a horizontal line at $200 and a straight
              line that starts at $0 with a slope of $200 per day. In "Adjusted" mode, we'll take
              the full booked amount ($2,000), subtract the already delivered impressions ($500, for
              a new total of $1,500), then divide that by the total number of remaining days (5) to
              get an ideal daily spend of $300. If we were to look at it for the entire 10 day
              range, on day one the ideal daily spend will be $200. After the first day (where we
              deliver $100) the remaining spend will be $1,900. With 9 days left the ideal daily
              spend will be $211. After we deliver another $100 the total will be $1,800, and with 8
              days left that makes the ideal daily spend $225. Every day the ideal spend increases
              because we're under-delivering when looking at the entire range. Once we get to day 5,
              the ideal daily spend will be $300. If they deliver $300 a day then the ideal will
              stay at $300 a day. If on the first day they deliver $400, then the new total will be
              $2,000 - $500 - $400 = $1,100, which divided by the remaining days (4) is $275.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowHelpModal(false)}>
              Got It
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default PacingDashboard;
